import { FC, useEffect, useState } from 'react';
import styles from './SubscribeForm.module.scss';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { subscribeFormSchema } from './yupSchema';
import { getFormData } from '@/src/lib/helpers/getFormData';
import { defaultEndpoint } from '@/src/lib/constants/constants';
import classNames from 'classnames';
import FormAgreements from '../formAgreements/FormAgreements';
import Button from '../button/Button';
import { LoaderIcon } from '@/src/components/ui/icons/loaderIcon/LoaderIcon';
import { SuccessIcon } from '@/src/components/ui/icons/successIcon/SuccessIcon';
import { ErrorIcon } from '@/src/components/ui/icons/errorIcon/ErrorIcon';

interface Inputs {
  Email: string;
  Theme: string;
  AGREEMENT: boolean;
  TYPE_FORM: string;
  PORTFOLIO: string;
}

interface SubscribeFormState {
  isLoading: boolean;
  submitted: boolean;
  isEmailSent: boolean;
}

interface SubscribeFormProps {
  colorScheme: 'light' | 'grey' | 'dark';
  theme?: string;
}

const SubscribeForm: FC<SubscribeFormProps> = ({ colorScheme, theme }) => {
  const [subscribeFormState, setSubscribeFormState] = useState<SubscribeFormState>({
    isLoading: false,
    submitted: false,
    isEmailSent: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Inputs>({
    mode: 'all',
    resolver: yupResolver(subscribeFormSchema),
  });

  const postData = async (data: Inputs) => {
    setSubscribeFormState((prev) => {
      return {
        ...prev,
        isLoading: true,
      };
    });

    const formData = getFormData(data);

    const endpoint = process.env.NEXT_PUBLIC_FORM_ENDPOINT || defaultEndpoint;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      setSubscribeFormState(() => {
        return {
          submitted: true,
          isEmailSent: true,
          isLoading: false,
        };
      });

      reset();
    } catch (error) {
      console.error('Error submitting the form:', error);

      setSubscribeFormState(() => {
        return {
          submitted: true,
          isEmailSent: false,
          isLoading: false,
        };
      });
    }
  };

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    postData(data);
  };

  const isValidationErrors = !!Object.keys(errors).length;

  useEffect(() => {
    return () => {
      setSubscribeFormState(() => ({
        isLoading: false,
        submitted: false,
        isEmailSent: false,
      }));
      reset();
    };
  }, []);

  const btnColorScheme =
    colorScheme === 'light' || colorScheme === 'grey' ? 'primary-dark' : 'primary-light';

  if (subscribeFormState.submitted) {
    return (
      <div className={styles.formSubmissionResult}>
        <div className={styles.formSubmissionResult__content}>
          <h2 className={styles.formSubmissionResult__title}>
            <span className={styles.formSubmissionResult__icon}>
              {subscribeFormState.isEmailSent ? <SuccessIcon /> : <ErrorIcon />}
            </span>
            {subscribeFormState.isEmailSent
              ? 'Success! You have been subscribed!'
              : 'Oops! Something went wrong!'}
          </h2>
          <p className={styles.formSubmissionResult__desc}>
            {subscribeFormState.isEmailSent
              ? 'Thank you for subscribing! Stay tuned for updates.'
              : 'There was an error trying to subscribe. Please try again later.'}
          </p>
          {!subscribeFormState.isEmailSent && (
            <Button
              action={'standard'}
              aria-label={'Try again button'}
              color={btnColorScheme}
              onClick={() =>
                setSubscribeFormState((prev) => ({
                  ...prev,
                  submitted: false,
                }))
              }>
              Try Again!
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <form
      action=""
      className={classNames(styles.form, styles[`form--${colorScheme}`])}
      onSubmit={handleSubmit(onSubmit)}
      noValidate>
      <div className={styles.formMain}>
        <input
          type="email"
          id="contact-email"
          placeholder="E-mail"
          className={classNames(styles.input, {
            [styles['input--error']]: errors.Email,
          })}
          aria-label="E-mail"
          {...register('Email', { required: true })}
        />
        <input type="hidden" {...register('Theme')} defaultValue={theme ?? 'None'} />
        <Button
          type="submit"
          aria-label="Subscribe"
          className={classNames(styles.button, {
            [styles[`button--isLoading`]]: subscribeFormState.isLoading,
            [styles[`button--locked`]]: isValidationErrors,
          })}
          action="interactive"
          color={btnColorScheme}
          isLoading={subscribeFormState.isLoading}>
          {subscribeFormState.isLoading ? <LoaderIcon /> : 'Subscribe'}
        </Button>
      </div>
      {errors.Email && <p className={styles.error}>{errors.Email.message}</p>}
      <FormAgreements colorScheme={colorScheme} register={register} errors={errors} />
    </form>
  );
};

export default SubscribeForm;
